import type { MaskedPlayer as Player } from '@completto/shared';
import { LANE_SIZE, laneRevealedCount } from '@completto/shared';
import { html, View } from 'rune-ts';
import { MiniLaneView } from './MiniLane.ts';

export type OpponentCardData = {
  player: Player;
  isCurrentTurn: boolean;
  isMe: boolean;
  recentlyActed: boolean;
  /** 1-based 표시 번호. 옵셔널 — 미지정 시 도트만 표시. */
  seatLabel?: number;
};

export class OpponentCardView extends View<OpponentCardData> {
  override template() {
    const { player, isCurrentTurn, isMe, recentlyActed, seatLabel } = this.data;
    const revealed = laneRevealedCount(player.lane);
    const progressPct = Math.round((revealed / LANE_SIZE) * 100);
    const mini = new MiniLaneView({ lane: player.lane, tone: 'opponent' });

    const isDisconnected = !player.connected;
    const baseCls = isCurrentTurn
      ? 'opponent-card relative flex flex-col gap-1.5 mlandscape:gap-1 rounded-lg bg-wood-paper ring-1 ring-card-gold/70 px-3 py-2 mlandscape:py-2 mlandscape:px-3 transition-all mlandscape:overflow-visible'
      : 'opponent-card relative flex flex-col gap-1.5 mlandscape:gap-1 rounded-lg bg-wood-paper/70 ring-1 ring-wood-sand/70 px-3 py-2 mlandscape:py-2 mlandscape:px-3 transition-all mlandscape:overflow-visible';
    const cardCls = isDisconnected ? `${baseCls} opacity-60 grayscale` : baseCls;
    const turnBar = isCurrentTurn
      ? html`<div class="absolute left-0 top-1 bottom-1 w-1 rounded-full bg-card-gold"></div>`
      : html``;

    const meBadge = isMe
      ? html`<span class="ml-1 text-caption text-card-brass">[나]</span>`
      : html``;
    const recentBadge = recentlyActed
      ? html`<span class="ml-1 text-card-brass text-caption animate-pulse">+1✨</span>`
      : html``;
    const disconnectBadge = isDisconnected
      ? html`<span class="ml-1 text-caption text-wood-coffee/70" title="연결 끊김">⚡</span>`
      : html``;

    const seatBadge =
      seatLabel !== undefined
        ? html`<span class="hidden mlandscape:inline-flex shrink-0 w-4 h-4 items-center justify-center rounded-full bg-card-navy text-card-gold text-[9px] font-bold leading-none">${seatLabel}</span>`
        : html``;

    const revealedNumbers = collectRevealedNumbers(player.lane);
    const numberList =
      revealedNumbers.length > 0
        ? html`<div class="flex flex-wrap gap-x-1.5 gap-y-0.5 text-caption text-wood-coffee tabular-nums mlandscape:hidden">
            <span class="text-wood-coffee/55">공개:</span>
            <span>${revealedNumbers.join(' · ')}</span>
          </div>`
        : html`<div class="text-caption text-wood-coffee/55 mlandscape:hidden">공개된 타일 없음</div>`;

    return html`
      <div class="${cardCls}">
        ${turnBar}
        <div class="flex items-center gap-1.5 min-w-0 mlandscape:gap-1 mlandscape:shrink-0">
          ${seatBadge}
          <span class="text-body mlandscape:text-[11px] font-semibold truncate text-wood-ink mlandscape:max-w-[88px]">
            ${player.nickname}${meBadge}${disconnectBadge}
          </span>
          <span class="text-caption text-wood-coffee tabular-nums whitespace-nowrap font-mono ml-auto mlandscape:hidden">
            ${revealed}/${LANE_SIZE}${recentBadge}
          </span>
          <span class="hidden mlandscape:inline text-[9px] text-wood-coffee/70 tabular-nums whitespace-nowrap font-mono shrink-0 ml-auto">
            ${revealed}/${LANE_SIZE}${recentBadge}
          </span>
        </div>
        <div class="h-1.5 rounded-full bg-wood-sand/60 overflow-hidden mlandscape:hidden">
          <div
            class="h-full bg-card-gold transition-all duration-300"
            style="width: ${progressPct}%"
          ></div>
        </div>
        <div class="opacity-90 overflow-x-auto mlandscape:w-full mlandscape:flex">${mini}</div>
        ${numberList}
      </div>
    `;
  }
}

function collectRevealedNumbers(lane: Player['lane']): number[] {
  const out: number[] = [];
  for (const slot of lane.slots) {
    if (slot.tile.face === 'UP' && slot.tile.resolvedNumber !== null) {
      out.push(slot.tile.resolvedNumber);
    }
  }
  return out;
}
